/**
 * **C01 · The AOD assessment package** (Stream C, Documents.dc.html — due November 15), built
 * literally to the drawing and scored against it at 72 px to the inch (`tools/paper-fidelity.ts`):
 * the cover letter and the P&L two ways. The layout is the `aod_package` engine's; the figures
 * are Money's engines', read through Money's own routes — the one-pager's year, the Year
 * setting's rate, the finance year's due date, Roles for the two named signers, and the
 * statement two ways (WORKLIST M16), which prints NO DATA until Money serves it.
 */
import type { ReactNode } from 'react';
import {
  aodPackage,
  yearFromOnePager,
  type AodRate,
  type TwoViewStatement,
} from '@cider/domain/engines';
import { useLoad, type Report } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

export interface RoleRow {
  name: string;
  role: string;
  state: string;
  named?: boolean;
}

/** The one person holding a role this year, by name — or nobody, and a hand writes it in. */
export function holder(rows: readonly RoleRow[] | null, role: string): string | null {
  const live = (rows ?? []).filter(
    (r) => r.role === role && (r.state === 'granted' || r.state === 'active') && r.named !== false,
  );
  return live.length === 1 ? (live[0]?.name ?? null) : null;
}

export function AodPackage({ fy }: { fy: number }): ReactNode {
  const one = useLoad<Report>(`/money/reports/one-pager?fy=${String(fy)}`);
  const settings = useLoad<{ archdioceseAssessment: AodRate | null }>(
    `/money/year-settings/${String(fy)}`,
  );
  // The package for a fiscal year is due in the next one's November (the finance year's `aod` row).
  const card = useLoad<{ steps: { key: string; due: string | null }[] }>(
    `/money/finance-year/${String(fy + 1)}`,
  );
  const statement = useLoad<TwoViewStatement>(`/money/statement-two-views/${String(fy)}`);
  // Signed in the November after the year closes, by the people in office that festival year.
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(fy)}`);
  const loads = [one, settings, card, statement, roles];
  const ready = loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-aod-package', ready, { year: fy });
  if (!ready) return <p>Reading the figures…</p>;

  const report = one.data?.status === 'ok' ? one.data : null;
  const pages = aodPackage({
    fiscalYear: fy,
    year: yearFromOnePager(report?.sections.find((s) => s.head === 'The year')?.rows ?? []),
    rate: settings.data ? settings.data.archdioceseAssessment : undefined,
    statement: statement.data && 'income' in statement.data ? statement.data : null,
    due: card.data?.steps.find((s) => s.key === 'aod')?.due ?? null,
    director: holder(roles.data, 'director'),
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={pages} />;
}
